import React, { useState, useEffect } from 'react';
import lodash from 'lodash';
import classnames from 'classnames';
import { useImmer } from 'use-immer';

import { ComposablePageHeader, MainContent } from 'components/common/PageHeader/PageHeader';

import styles from './CreateEventType.scss';
import {
    PageBody,
    EventDetails,
    EventDefinition,
    PrimaryDateSettings,
    DisplaySettings,
    IntegrationSettings,
    ActionButtons,
} from './components';
import { GoBack } from 'components/common/Link';
import { Conditional } from 'common/components/layout';

export default function CreateEventType(props) {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [details, setDetails] = useImmer([]);
    const [counter, setCounter] = useState(0);
    const [selectedIntegrations, setSelectedIntegrations] = useState([]);

    const [primaryDateDetail, setPrimaryDateDetail] = useState(null);
    const [firstDisplayDetail, setFirstDisplayDetail] = useState(null);
    const [secondDisplayDetail, setSecondDisplayDetail] = useState(null);
    const [thirdDisplayDetail, setThirdDisplayDetail] = useState(null);

    const onDetailChanged = (setDetail, detail) => {
        if (detail !== null) {
            if (primaryDateDetail !== null && primaryDateDetail.value === detail.value) {
                setPrimaryDateDetail(null);
            } else if (firstDisplayDetail !== null && firstDisplayDetail.value === detail.value) {
                setFirstDisplayDetail(null);
            } else if (secondDisplayDetail !== null && secondDisplayDetail.value === detail.value) {
                setSecondDisplayDetail(null);
            } else if (thirdDisplayDetail !== null && thirdDisplayDetail.value === detail.value) {
                setThirdDisplayDetail(null);
            }
        }
        setDetail(detail);
    };

    useEffect(() => {
        props.getInstitutionDataSources(props.institutionId);
        props.fetchApiIntegrations(props.institutionId);
        props.getInstitutionEventTypes(props.institutionId);

        return () => {
            props.removeInstitutionDataSources();
            props.cleanApiIntegrations();
            props.clearInstitutionEventTypes();
        };
    }, []);

    const createEventType = () => {
        const slatOrder = getSlatOrder(firstDisplayDetail, secondDisplayDetail, thirdDisplayDetail);
        const selectedIntegrationPartnerIds = lodash.map(selectedIntegrations, ({ id }) => id);

        const eventType = {
            displayName: title,
            title: sanitize(title),
            description,
            details: details.map(detail => ({ ...detail, title: sanitize(detail.title), displayName: detail.title })),
            primaryDateField: sanitize(primaryDateDetail.label),
            slatOrder: slatOrder,
            integrationPartnerIds: selectedIntegrationPartnerIds,
        };

        props.createEventType(props.institutionId, eventType);
        props.pushRoute();
    };

    const addEventDetail = () => {
        const detail = {
            id: `item-${counter}`,
            title: '',
            label: '',
            description: '',
            view: '',
            placeholder: null,
            enableOtherOption: false,
            ordinal: counter,
        };

        setDetails(draft => {
            draft.push(detail);
        });

        setCounter(counter + 1);
    };

    const removeEventDetail = (removedEventDetailId) => {
        setDetails(draft => {
            return draft.filter(detail => detail.id !== removedEventDetailId);
        });

        if (primaryDateDetail !== null && primaryDateDetail.value === removedEventDetailId) {
            setPrimaryDateDetail(null);
        } else if (firstDisplayDetail !== null && firstDisplayDetail.value === removedEventDetailId) {
            setFirstDisplayDetail(null);
        } else if (secondDisplayDetail !== null && secondDisplayDetail.value === removedEventDetailId) {
            setSecondDisplayDetail(null);
        } else if (thirdDisplayDetail !== null && thirdDisplayDetail.value === removedEventDetailId) {
            setThirdDisplayDetail(null);
        }
    };

    const onEventDetailsOrderChanged = (orderedEventDetails) => {
        const reorderedEventDetails = orderedEventDetails.map((item, index) => {
            return {
                ...item,
                ordinal: index
            };
        });
        setDetails(() => {
            return reorderedEventDetails;
        });
    };

    const updateEventDetailState = (detailId, property, value) => {
        setDetails(draft => {
            const index = draft.findIndex(detail => detail.id === detailId);
            let detail = draft[index];
            detail[property] = value;
        });

        if (primaryDateDetail && detailId === primaryDateDetail.value && property === 'view') {
            onDetailChanged(setPrimaryDateDetail, null);
        }
    };

    return (
        <main className={classnames(styles.parent, props.className)} data-cy="CreateEventType-main">
            <GoBack onClick={props.pushRoute} className={styles.goBack} data-cy="CreateEventType-GoBack">
                Back to Event Types
            </GoBack>
            <ComposablePageHeader className={styles.title}>
                <MainContent>
                    <h1 className={styles.title}>Create New Event</h1>
                </MainContent>
            </ComposablePageHeader>

            <PageBody>
                <EventDefinition
                    title={title}
                    isTitleValid={() => isValidTitle(title, props.institutionEventTypes)}
                    onTitleChange={setTitle}
                    description={description}
                    onDescriptionChange={setDescription}
                />

                <EventDetails
                    dataSources={props.dataSources}
                    details={details}
                    title={title}
                    onItemOrderChanged={onEventDetailsOrderChanged}
                    onRemoveItem={removeEventDetail}
                    onItemChanged={updateEventDetailState}
                    onAddEventDetail={addEventDetail}
                />

                <PrimaryDateSettings
                    dateDetailOptions={getDateDetails(details)}
                    setPrimaryDateDetail={setPrimaryDateDetail}
                    onDetailChanged={onDetailChanged}
                    primaryDateDetail={primaryDateDetail}
                />

                <DisplaySettings
                    displaySettingsOptions={getSelectableDisplayDetails(details, primaryDateDetail)}
                    primaryDateDetail={primaryDateDetail}
                    firstDisplayDetail={firstDisplayDetail}
                    secondDisplayDetail={secondDisplayDetail}
                    thirdDisplayDetail={thirdDisplayDetail}
                    setFirstDisplayDetail={setFirstDisplayDetail}
                    setSecondDisplayDetail={setSecondDisplayDetail}
                    setThirdDisplayDetail={setThirdDisplayDetail}
                    onDetailChanged={onDetailChanged}
                />

                <Conditional condition={!lodash.isEmpty(props.enabledIntegrations)}>
                    <IntegrationSettings
                        integrationOptions={props.enabledIntegrations}
                        selectedIntegrations={selectedIntegrations}
                        setSelectedIntegration={setSelectedIntegrations}
                    />
                </Conditional>

                <ActionButtons
                    onCancelClicked={props.pushRoute}
                    onCreateClicked={createEventType}
                    createDisabled={validateForm({
                        eventTypes: props.institutionEventTypes,
                        title,
                        details,
                        primaryDateDetail
                    })}
                />
            </PageBody>
        </main>
    );
}

export const getDateDetails = (details) => details.filter(detail => detail.view === 'DatePicker');

export const getSelectableDisplayDetails = (details, primaryDateDetail) => details.filter(
    detail => primaryDateDetail === null || detail.id !== primaryDateDetail.value
);

export const sanitize = (input) => input.trim().toLowerCase().replace(/\s/g, '_');

export const getSlatOrder = (displayDetail1, displayDetail2, displayDetail3) => [
    displayDetail1,
    displayDetail2,
    displayDetail3
]
    .filter(detail => detail && detail.label)
    .map(detail => sanitize(detail.label));

export const isDuplicateEventTypeName = (eventTypes, name) => eventTypes.some(eventType => eventType.name === sanitize(name));


export const formValidators = {
    nonEmptyTitle: ({ title }) => lodash.isEmpty(title),
    uniqueTitle: ({ eventTypes, title }) => isDuplicateEventTypeName(eventTypes, title),
    detailsWithMissingFields: ({ details }) => details.some(
        detail => lodash.isEmpty(detail.title) || lodash.isEmpty(detail.label)
    ),
    fieldTitlesNotUnique: ({ details }) => {
        const allFieldTitles = details.map(detail => detail.title);
        const uniqueFieldTitles = new Set(allFieldTitles);
        return allFieldTitles.length !== uniqueFieldTitles.size;
    },
    eventDetails: ({ details }) => {
        const singleSelectDetails = details.filter(detail => detail.view === 'SingleSelect');

        for (const detail of singleSelectDetails) {
            if (!detail.option) {
                return true;
            }

            if (detail.option === 'Custom List') {
                return lodash.isNil(detail.options);
            }

            if (detail.option === 'System Data') {
                return lodash.isNil(detail.dataSource);
            }
        }

        return false;
    },
    primaryDateIsSet: ({ primaryDateDetail }) => {
        return lodash.isNil(primaryDateDetail);
    }
};

export const isValidTitle = (title, eventTypes) =>
    !formValidators.nonEmptyTitle({ title }) &&
    !formValidators.uniqueTitle({ eventTypes, title });

export const validateForm = (data) => Object.values(formValidators).some(validator => validator(data));
